import Axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponseHeaders,
} from "axios";
import { STORAGE_KEYS, HTTP_STATUS } from "@/constants/constants";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { ROUTES } from "@/constants/routes";
import { env } from "./env";

export const AXIOS_INSTANCE = Axios.create({
  baseURL: env.API_BASE_URL,
  timeout: env.API_TIMEOUT,
  withCredentials: true, // allow cookies (HttpOnly) if backend sets them
});

// Holds a reference to Auth0's getIdTokenClaims, set by AuthLayout on mount.
// This allows the interceptor to silently refresh the token when it's near expiry
// instead of reading a potentially stale value from sessionStorage.
type GetIdTokenClaims = () => Promise<{ __raw: string; exp?: number } | undefined>;
let _getIdTokenClaims: GetIdTokenClaims | null = null;

export function setGetIdTokenClaims(fn: GetIdTokenClaims) {
  _getIdTokenClaims = fn;
}

const TOKEN_REFRESH_BUFFER_SECONDS = 60; // refresh if expiring within 60 s

// Attach Auth0 ID token to requests, refreshing silently when near expiry
AXIOS_INSTANCE.interceptors.request.use(
  async (config) => {
    try {
      if (_getIdTokenClaims) {
        // Check the cached token's exp claim before deciding whether to refresh
        const cached = sessionStorage.getItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
        let needsRefresh = !cached;

        if (cached) {
          try {
            const [, payloadB64] = cached.split(".");
            const { exp } = JSON.parse(atob(payloadB64)) as { exp?: number };
            if (exp) {
              const secondsUntilExpiry = exp - Math.floor(Date.now() / 1000);
              needsRefresh = secondsUntilExpiry < TOKEN_REFRESH_BUFFER_SECONDS;
            }
          } catch {
            needsRefresh = true; // malformed token — force refresh
          }
        }

        if (needsRefresh) {
          const claims = await _getIdTokenClaims();
          const fresh = claims?.__raw;
          if (fresh) {
            sessionStorage.setItem(STORAGE_KEYS.AUTH0_RAW_TOKEN, fresh);
            if (!config.headers) config.headers = {} as any;
            config.headers.Authorization = `Bearer ${fresh}`;
            return config;
          }
        }
      }

      // Fall back to cached token
      const auth0Token = sessionStorage.getItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
      if (auth0Token) {
        if (!config.headers) config.headers = {} as any;
        config.headers.Authorization = `Bearer ${auth0Token}`;
      }
      return config;
    } catch (error) {
      console.error("Error in request interceptor:", error);
      throw error;
    }
  },
  (error) => {
    console.error("Error in request interceptor:", error);
    throw error;
  }
);

// Add response interceptor to handle common error cases
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      // Clear the invalid tokens
      sessionStorage.removeItem(STORAGE_KEYS.AUTH0_RAW_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.USER_PROFILE);

      // Check if we're on the webchat page - if so, don't redirect automatically
      // Let the webchat component handle the error gracefully
      if (window.location.pathname === ROUTES.WEBCHAT) {
        throw new Error(UI_MESSAGES.SESSION_EXPIRED);
      }

      // For other pages, redirect to login or refresh the page
      window.location.href = ROUTES.ROOT;
      throw new Error(UI_MESSAGES.SESSION_EXPIRED);
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
      throw new Error(UI_MESSAGES.NO_PERMISSION);
    }

    // Handle 404 with "Access Denied" message (special case where backend returns 404 for permission issues)
    if (error.response?.status === 404) {
      const responseData = error.response.data as any;
      if (responseData?.message === 'Access Denied' || responseData?.error === 'NOT_FOUND') {
        // Don't convert to generic error - preserve the original error for proper handling
        // The error will be caught and displayed by components using error-utils
        throw error;
      }
    }

    // Handle network errors
    if (!error.response) {
      throw new Error(UI_MESSAGES.NETWORK_ERROR);
    }

    // For all other errors, throw the original error so it can be properly processed
    throw error;
  }
);

// add a second `options` argument here if you want to pass extra options to each generated query
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig & { signal?: AbortSignal }
): Promise<{ data: T; headers: AxiosResponseHeaders }> => {
  return AXIOS_INSTANCE({
    ...config,
    ...options,
    // Wire React Query's AbortSignal directly — Axios 1.x supports this natively
    signal: options?.signal ?? config.signal,
  }).then(({ data, headers }) => ({
    data: data as T,
    headers: headers as AxiosResponseHeaders,
  }));
};

type ApiErrorResponse = {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
};

// Then when using your ErrorType wrapper
export type ErrorType<Error = ApiErrorResponse> = AxiosError<Error>;

// export type BodyType<BodyData> = BodyData;

// // Or, in case you want to wrap the body type (optional)
// // (if the custom instance is processing data before sending it, like changing the case for example)
// export type BodyType<BodyData> = CamelCase<BodyData>;
